import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { selectAdminToken } from './sessionSlice';

const API = process.env.REACT_APP_BACKEND_URL;

// Small helper to POST JSON with the admin bearer token.
async function postJson(path, body, token) {
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body || {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Request failed: ${path}`);
  }
  return json;
}

// Resolve the current theme (themename) from the theme slice. Every PulsePlay
// 2.0 session is scoped to a theme, so this must be present.
function currentThemeOf(state) {
  return state.theme?.currentTheme || state.theme?.data?.themename || null;
}

// --- Thunks ---------------------------------------------------------------

export const fetchSession = createAsyncThunk(
  'sessionBuilder/fetchSession',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = selectAdminToken(state);
      const currentTheme = currentThemeOf(state);
      if (!currentTheme) throw new Error('No theme selected');
      const json = await postJson('/pp2/getSession', { currentTheme }, token);
      return json.data; // { session, activities }
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const saveSession = createAsyncThunk(
  'sessionBuilder/saveSession',
  async (settings, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = selectAdminToken(state);
      const currentTheme = currentThemeOf(state);
      if (!currentTheme) throw new Error('No theme selected');
      const json = await postJson('/pp2/upsertSession', { ...settings, currentTheme }, token);
      return json.data; // session row
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const addActivity = createAsyncThunk(
  'sessionBuilder/addActivity',
  async ({ type, title = '', timer_seconds = 30 }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = selectAdminToken(state);
      const currentTheme = currentThemeOf(state);
      if (!currentTheme) throw new Error('No theme selected');
      const json = await postJson(
        '/pp2/addActivity',
        { type, title, timer_seconds, currentTheme },
        token
      );
      return json.data; // activity row
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateActivity = createAsyncThunk(
  'sessionBuilder/updateActivity',
  async ({ activity_id, title, timer_seconds }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = selectAdminToken(state);
      const currentTheme = currentThemeOf(state);
      if (!currentTheme) throw new Error('No theme selected');
      const json = await postJson(
        '/pp2/updateActivity',
        { activity_id, title, timer_seconds, currentTheme },
        token
      );
      return json.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteActivity = createAsyncThunk(
  'sessionBuilder/deleteActivity',
  async ({ activity_id }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = selectAdminToken(state);
      const currentTheme = currentThemeOf(state);
      if (!currentTheme) throw new Error('No theme selected');
      await postJson('/pp2/deleteActivity', { activity_id, currentTheme }, token);
      return activity_id;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const reorderActivities = createAsyncThunk(
  'sessionBuilder/reorderActivities',
  async ({ ordered_ids }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token = selectAdminToken(state);
      const currentTheme = currentThemeOf(state);
      if (!currentTheme) throw new Error('No theme selected');
      const json = await postJson('/pp2/reorderActivities', { ordered_ids, currentTheme }, token);
      return json.data; // reordered activities
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// --- Slice ----------------------------------------------------------------

const initialState = {
  session: null, // pp_sessions row
  activities: [], // pp_activities rows (ordered)
  status: 'idle',
  saving: false,
  error: null,
};

const sessionBuilderSlice = createSlice({
  name: 'sessionBuilder',
  initialState,
  reducers: {
    // Optimistic local reorder (UI drag) before persisting.
    setActivitiesLocal(state, action) {
      state.activities = action.payload;
    },
    clearBuilderError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSession.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSession.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.session = action.payload.session;
        state.activities = action.payload.activities || [];
      })
      .addCase(fetchSession.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      .addCase(saveSession.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveSession.fulfilled, (state, action) => {
        state.saving = false;
        state.session = action.payload;
      })
      .addCase(saveSession.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(addActivity.fulfilled, (state, action) => {
        state.activities.push(action.payload);
      })
      .addCase(addActivity.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateActivity.fulfilled, (state, action) => {
        const idx = state.activities.findIndex((a) => a.id === action.payload.id);
        if (idx !== -1) state.activities[idx] = action.payload;
      })

      .addCase(deleteActivity.fulfilled, (state, action) => {
        state.activities = state.activities
          .filter((a) => a.id !== action.payload)
          .map((a, i) => ({ ...a, sort_order: i }));
      })

      .addCase(reorderActivities.fulfilled, (state, action) => {
        state.activities = action.payload;
      });
  },
});

export const { setActivitiesLocal, clearBuilderError } = sessionBuilderSlice.actions;
export default sessionBuilderSlice.reducer;

// Selectors
export const selectSession = (state) => state.sessionBuilder.session;
export const selectActivities = (state) => state.sessionBuilder.activities;
export const selectBuilderStatus = (state) => state.sessionBuilder.status;
